// frontend/src/pages/Orders/OrdersList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedComponent from '../../components/ProtectedComponent';
import { PERMISSIONS } from '../../utils/permissions';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  InputGroup,
  InputGroupText,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from 'reactstrap';
import { ordersAPI } from '../../services/api';
import { parseDate } from '../../utils/dates';

function OrdersList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTime, setFilterTime] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (amount) =>
    `$${parseFloat(amount || 0).toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    loadOrders();
  }, [filterTime, customDateRange]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      
      const now = new Date();
      if (filterTime === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        params.created_after = weekAgo.toISOString().split('T')[0];
      } else if (filterTime === 'month') {
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        params.created_after = monthAgo.toISOString().split('T')[0];
      } else if (filterTime === 'custom' && customDateRange.start && customDateRange.end) {
        params.created_after = customDateRange.start;
        params.created_before = customDateRange.end;
      }
      // Si filterTime === 'all', params queda vacío (sin filtros)
      
      console.log('📊 Cargando órdenes con filtros:', params);
      
      const response = await ordersAPI.getAll(params);
      const data = response.data.results || response.data;
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const applyCustomFilter = () => {
    if (!customDateRange.start || !customDateRange.end) {
      alert('Por favor selecciona ambas fechas');
      return;
    }
    setFilterTime('custom');
    setShowCustomModal(false);
    loadOrders();
  };

  const clearCustomFilter = () => {
    setCustomDateRange({ start: '', end: '' });
    setFilterTime('all');
    setShowCustomModal(false);
  };

  const filteredOrders = orders.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (order.order_number && order.order_number.toLowerCase().includes(searchLower)) ||
      (order.billing_name && order.billing_name.toLowerCase().includes(searchLower)) ||
      (order.quote_proposal_number && order.quote_proposal_number.toLowerCase().includes(searchLower))
    );
  });

  const totalOrders = orders.length;
  const withQuote = orders.filter((o) => o.quote).length;
  const withoutQuote = orders.filter((o) => !o.quote).length;
  const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.subtotal || 0), 0);

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando Ã³rdenes...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      {/* âœ… Header con gradiente corporativo */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* âœ… Stats Cards con iconos y sombras */}
        <Row>
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Total Ordenes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{totalOrders}</h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          <i className="ni ni-bold-up me-1"></i>+15%
                        </span>
                        <span className="text-xs text-muted"> vs mes anterior</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-box-2 text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Con propuesta</p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{withQuote}</h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {totalOrders > 0 ? Math.round((withQuote / totalOrders) * 100) : 0}%
                        </span>
                        <span className="text-xs text-muted"> vinculadas</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-check-bold text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Sin Propuesta</p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{withoutQuote}</h5>
                      <p className="mb-0">
                        <span className="text-warning text-sm font-weight-bolder">
                          {totalOrders > 0 ? Math.round((withoutQuote / totalOrders) * 100) : 0}%
                        </span>
                        <span className="text-xs text-muted"> huÃ©rfanas</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-fat-remove text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Monto Total</p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        ${totalAmount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          <i className="ni ni-bold-up me-1"></i>+10%
                        </span>
                        <span className="text-xs text-muted"> vs mes anterior</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-danger shadow-danger text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* âœ… Tabla de Ã³rdenes con diseÃ±o mejorado */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              {/* âœ… Header con gradiente */}
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-box-2 me-2"></i>
                      Lista de ordenes de Compra
                    </h6>
                  </Col>
                  <Col md="6" className="text-end">
                    <ProtectedComponent permission={PERMISSIONS.CREATE_ORDERS}>
                      <Button 
                        color="white"
                        size="sm"
                        className="btn-icon shadow-sm"
                        onClick={() => navigate('/admin/inbox/orders')}
                      >
                        <span className="btn-inner--icon">
                          <i className="ni ni-fat-add"></i>
                        </span>
                        <span className="btn-inner--text ms-2">Nueva Orden</span>
                      </Button>
                    </ProtectedComponent>
                  </Col>
                </Row>
                
                {/* âœ… Filtros con estilo mejorado */}
                <Row className="mt-3">
                  <Col md="6">
                    <InputGroup className="input-group-alternative shadow-sm">
                      <InputGroupText className="bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </InputGroupText>
                      <Input
                        type="text"
                        className="form-control-alternative"
                        placeholder="Buscar por nÃºmero de orden, cliente o propuesta..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md="6">
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="text-sm me-2 text-white opacity-8">
                        <i className="ni ni-calendar-grid-58 me-1"></i>
                        Periodo:
                      </span>
                      <ButtonGroup size="sm">
                        <Button
                          color={filterTime === 'all' ? 'white' : 'white'}
                          outline={filterTime !== 'all'}
                          onClick={() => {
                            setFilterTime('all');
                            setCustomDateRange({ start: '', end: '' });
                          }}
                          className="shadow-sm"
                        >
                          <i className="ni ni-world me-1"></i>
                          Por Vida
                        </Button>
                        <Button
                          color={filterTime === 'month' ? 'white' : 'white'}
                          outline={filterTime !== 'month'}
                          onClick={() => {
                            setFilterTime('month');
                            setCustomDateRange({ start: '', end: '' });
                          }}
                          className="shadow-sm"
                        >
                          <i className="ni ni-calendar-grid-58 me-1"></i>
                          Este Mes
                        </Button>
                        <Button
                          color={filterTime === 'week' ? 'white' : 'white'}
                          outline={filterTime !== 'week'}
                          onClick={() => {
                            setFilterTime('week');
                            setCustomDateRange({ start: '', end: '' });
                          }}
                          className="shadow-sm"
                        >
                          <i className="ni ni-time-alarm me-1"></i>
                          Esta Semana
                        </Button>
                        <Button
                          color={filterTime === 'custom' ? 'white' : 'white'}
                          outline={filterTime !== 'custom'}
                          onClick={() => setShowCustomModal(true)}
                          className="shadow-sm"
                        >
                          <i className="ni ni-settings me-1"></i>
                          Custom
                        </Button>
                      </ButtonGroup>
                      
                      {filterTime === 'custom' && customDateRange.start && customDateRange.end && (
                        <Badge color="white" className="ms-2 shadow-sm">
                          <span className="text-dark">
                            {parseDate(customDateRange.start).toLocaleDateString('es-MX')} - {parseDate(customDateRange.end).toLocaleDateString('es-MX')}
                          </span>
                          <i 
                            className="fas fa-times ms-2 cursor-pointer text-danger" 
                            onClick={clearCustomFilter}
                            style={{ cursor: 'pointer' }}
                          ></i>
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          # Orden
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Cliente
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Propuesta
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Monto
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Facturacion
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Match
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Fecha
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <i className="ni ni-box-2 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-xs text-secondary mb-0 mt-3">
                              {searchTerm
                                ? 'No se encontraron Ã³rdenes con ese criterio'
                                : 'No hay Ã³rdenes registradas'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order) => {
                          const orderTotal = parseFloat(order.subtotal || 0);
                          const parsedScheduled = parseFloat(order.scheduled_amount_total);
                          const scheduledTotal = Number.isFinite(parsedScheduled) ? parsedScheduled : 0;

                          const parsedRemaining = parseFloat(order.remaining_to_schedule);
                          const remaining = Number.isFinite(parsedRemaining)
                            ? Math.max(parsedRemaining, 0)
                            : Math.max(orderTotal - scheduledTotal, 0);

                          const progressPercent =
                            orderTotal > 0 ? Math.min((scheduledTotal / orderTotal) * 100, 100) : 0;

                          const invoiceStatus =
                            scheduledTotal <= 0
                              ? { label: 'Pendiente', className: 'bg-gradient-secondary' }
                              : remaining > 0
                                ? { label: 'Parcial', className: 'bg-gradient-warning' }
                                : { label: 'Completa', className: 'bg-gradient-primary' };

                          return (
                            <tr 
                              key={order.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                            >
                              <td>
                                <div className="d-flex px-2 py-1">
                                  <div>
                                    <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                      <i className="ni ni-box-2 text-white text-xs"></i>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm text-dark">{order.order_number}</h6>
                                    <p className="text-xs text-secondary mb-0">
                                      <i className="ni ni-calendar-grid-58 me-1"></i>
                                      {order.order_date ? parseDate(order.order_date).toLocaleDateString('es-MX') : 'Sin fecha'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <p className="text-sm font-weight-bold mb-0 text-dark">
                                  {order.billing_name || order.quote_client_name || 'N/A'}
                                </p>
                                
                              </td>
                              <td className="align-middle text-center">
                                {order.quote ? (
                                  <div>
                                    <Badge color="" className="badge-sm bg-gradient-primary shadow-sm">
                                      <i className="ni ni-check-bold me-1"></i>
                                      Vinculada
                                    </Badge>
                                    <p className="text-xs text-secondary mb-0 mt-1">
                                      {order.quote_proposal_number}
                                    </p>
                                  </div>
                                ) : (
                                  <Badge color="" className="badge-sm bg-gradient-warning shadow-sm">
                                    <i className="ni ni-fat-remove me-1"></i>
                                    Sin propuesta
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-dark text-sm font-weight-bold">
                                  {formatCurrency(order.subtotal)}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                <div className="d-flex flex-column align-items-center px-2">
                                  <Badge color="" className={`badge-sm shadow-sm mb-1 ${invoiceStatus.className}`}>
                                    {invoiceStatus.label}
                                  </Badge>
                                  <p className="text-xs text-secondary mb-1">
                                    Prog: {formatCurrency(scheduledTotal)} | Pend: {formatCurrency(remaining)}
                                  </p>
                                  <div className="progress w-100" style={{ maxWidth: '170px', height: '6px' }}>
                                    <div
                                      className="progress-bar bg-info"
                                      role="progressbar"
                                      style={{ width: `${progressPercent}%` }}
                                      aria-valuenow={progressPercent}
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    ></div>
                                  </div>
                                </div>
                              </td>
                              <td className="align-middle text-center">
                                {order.matched_by_amount ? (
                                  <Badge color="" className="badge-sm bg-gradient-info shadow-sm">
                                    <i className="ni ni-settings me-1"></i>
                                    {order.match_with_tax ? 'Con IVA' : 'Exacto'}
                                  </Badge>
                                ) : (
                                  <Badge color="" className="badge-sm bg-gradient-secondary shadow-sm">
                                    <i className="ni ni-single-02 me-1"></i>
                                    Manual
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {parseDate(order.created_at).toLocaleDateString('es-MX')}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="btn-icon shadow-sm"
                            
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/orders/${order.id}`);
                                  }}
                                >
                                  <span className="btn-inner--icon">
                                    <i className=""></i>
                                    VER ORDEN
                                  </span>
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* âœ… Modal Custom Date Range con diseÃ±o mejorado */}
      <Modal isOpen={showCustomModal} toggle={() => setShowCustomModal(false)} centered>
        <ModalHeader 
          toggle={() => setShowCustomModal(false)}
          className="bg-gradient-primary pb-3"
        >
          <h6 className="mb-0 text-white">
            <i className="ni ni-calendar-grid-58 me-2"></i>
            Filtro de Fechas Personalizado
          </h6>
        </ModalHeader>
        <ModalBody className="p-4">
          <Row>
            <Col md="6">
              <Label className="form-control-label text-dark">
                <i className="ni ni-bold-right me-1"></i>
                Fecha Inicio
              </Label>
              <Input
                type="date"
                className="form-control-alternative shadow-sm"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
              />
            </Col>
            <Col md="6">
              <Label className="form-control-label text-dark">
                <i className="ni ni-bold-left me-1"></i>
                Fecha Fin
              </Label>
              <Input
                type="date"
                className="form-control-alternative shadow-sm"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
              />
            </Col>
          </Row>
          <div className="alert alert-info mt-3 shadow-sm">
            <span className="alert-icon">
              <i className="ni ni-bell-55"></i>
            </span>
            <span className="alert-text text-xs">
              Se filtrarÃ¡ por fecha de creaciÃ³n de la orden
            </span>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="secondary" 
            size="sm" 
            className="btn-icon shadow-sm"
            onClick={clearCustomFilter}
          >
            <span className="btn-inner--icon">
              <i className="ni ni-fat-remove"></i>
            </span>
            <span className="btn-inner--text ms-2">Limpiar</span>
          </Button>
          <Button 
            color="primary" 
            size="sm" 
            className="btn-icon shadow-sm"
            onClick={applyCustomFilter}
          >
            <span className="btn-inner--icon">
              <i className="ni ni-check-bold"></i>
            </span>
            <span className="btn-inner--text ms-2">Aplicar Filtro</span>
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default OrdersList;

